import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Loader2, Plus, Save, Trash2, Zap, X, ChevronDown, ChevronRight,
  Euro,
} from 'lucide-react';

/**
 * Paramètres → Obligés CEE
 * Gère la liste des obligés/délégataires + leur mention RAI + leurs
 * tarifs CEE (classique / précarité) par fiche.
 *
 * Fusionné avec l'ancienne page /cee/tarifs (menu supprimé) pour éviter
 * la duplication.
 */
export default function CeeObligesSettingsCard() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [draft, setDraft] = useState({});
  const [creating, setCreating] = useState(false);
  const [newItem, setNewItem] = useState({ nom: '', siren: '', mention_rai: '' });
  const [tarifs, setTarifs] = useState([]); // tous tarifs — filtrés par delegataire_id

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ro, rt] = await Promise.all([
        api.get('/cee-obliges'),
        api.get('/cee/tarifs'),
      ]);
      setItems(ro.data.items || []);
      setTarifs(rt.data.tarifs || []);
    } catch (e) {
      toast.error('Impossible de charger les obligés / tarifs CEE');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const beginEdit = (o) => {
    setExpandedId(o.oblige_id);
    setDraft({ nom: o.nom, siren: o.siren, mention_rai: o.mention_rai });
  };

  const saveEdit = async (id) => {
    try {
      await api.put(`/cee-obliges/${id}`, draft);
      toast.success('Obligé mis à jour');
      setExpandedId(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec sauvegarde');
    }
  };

  const remove = async (id, nom) => {
    if (!window.confirm(`Supprimer l'obligé "${nom}" ? Les devis existants conservent le snapshot.`)) return;
    try {
      await api.delete(`/cee-obliges/${id}`);
      toast.success('Obligé supprimé');
      load();
    } catch (e) {
      toast.error('Échec suppression');
    }
  };

  const create = async () => {
    if (!newItem.nom.trim()) { toast.error('Nom obligatoire'); return; }
    try {
      await api.post('/cee-obliges', newItem);
      toast.success('Obligé ajouté');
      setNewItem({ nom: '', siren: '', mention_rai: '' });
      setCreating(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec création');
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="cee-settings-card">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
            <Zap className="w-5 h-5 text-red-600" /> Obligés CEE & Tarifs
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            Gère la liste des obligés / délégataires CEE, leur mention RAI et leurs tarifs (classique
            / précarité) par fiche. Utilise
            <code className="text-red-600 bg-red-50 px-1 rounded mx-1">{'{montant}'}</code>,
            <code className="text-red-600 bg-red-50 px-1 rounded mx-1">{'{obligé}'}</code> ou
            <code className="text-red-600 bg-red-50 px-1 rounded mx-1">{'{siren}'}</code> dans la
            mention RAI — ils sont remplacés dynamiquement dans chaque PDF.
          </p>
        </div>
        <Button onClick={() => setCreating(v => !v)} variant="outline" size="sm" data-testid="cee-add-btn">
          {creating ? <><X className="w-4 h-4 mr-1.5" /> Annuler</> : <><Plus className="w-4 h-4 mr-1.5" /> Nouvel obligé</>}
        </Button>
      </div>

      {creating && (
        <div className="mb-4 rounded-xl border-2 border-red-200 bg-red-50/40 p-4 space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Nom obligé *">
              <Input value={newItem.nom} onChange={e => setNewItem({ ...newItem, nom: e.target.value })}
                     placeholder="ex : EDF" data-testid="cee-new-nom" />
            </Field>
            <Field label="SIREN">
              <Input value={newItem.siren} onChange={e => setNewItem({ ...newItem, siren: e.target.value })}
                     placeholder="ex : 552 081 317" data-testid="cee-new-siren" />
            </Field>
          </div>
          <Field label="Mention RAI">
            <Textarea rows={5} value={newItem.mention_rai}
                      onChange={e => setNewItem({ ...newItem, mention_rai: e.target.value })}
                      placeholder="ex : L'entreprise Reno Resolve, mandataire de {obligé}, a joué un rôle actif et incitatif... Prime CEE : {montant}..."
                      data-testid="cee-new-mention" />
            <PlaceholderChips onInsert={(ph) => setNewItem({ ...newItem, mention_rai: (newItem.mention_rai || '') + ph })} />
          </Field>
          <div className="flex justify-end">
            <Button onClick={create} className="bg-red-600 hover:bg-red-700 text-white" data-testid="cee-new-save">
              <Save className="w-4 h-4 mr-1.5" /> Enregistrer
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center py-8 text-slate-500"><Loader2 className="w-5 h-5 mx-auto animate-spin" /></div>
      ) : items.length === 0 ? (
        <p className="text-sm text-slate-500 text-center py-8">Aucun obligé configuré.</p>
      ) : (
        <div className="space-y-2">
          {items.map(o => {
            const isOpen = expandedId === o.oblige_id;
            const ownTarifs = tarifs.filter((t) => t.delegataire_id === o.oblige_id);
            return (
              <div key={o.oblige_id} className="rounded-lg border border-slate-200 bg-white overflow-hidden"
                   data-testid={`cee-oblige-${o.oblige_id}`}>
                <button onClick={() => isOpen ? setExpandedId(null) : beginEdit(o)}
                        className="w-full flex items-center justify-between p-3 hover:bg-slate-50 text-left">
                  <div className="flex items-center gap-2 min-w-0">
                    {isOpen ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                    <span className="font-semibold text-sm text-slate-900">{o.nom}</span>
                    {o.siren && <span className="text-xs text-slate-500">SIREN {o.siren}</span>}
                    {ownTarifs.length > 0 && (
                      <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded ml-1">
                        {ownTarifs.length} tarif{ownTarifs.length > 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                  {!isOpen && (
                    <span className="text-[11px] text-slate-400 max-w-[400px] truncate italic">
                      {o.mention_rai || <em className="opacity-50">pas de mention RAI</em>}
                    </span>
                  )}
                </button>

                {isOpen && (
                  <div className="border-t border-slate-200 p-4 bg-slate-50/50 space-y-4">
                    <div className="grid sm:grid-cols-2 gap-3">
                      <Field label="Nom obligé *">
                        <Input value={draft.nom} onChange={e => setDraft({ ...draft, nom: e.target.value })} />
                      </Field>
                      <Field label="SIREN">
                        <Input value={draft.siren} onChange={e => setDraft({ ...draft, siren: e.target.value })} />
                      </Field>
                    </div>
                    <Field label="Mention RAI (Rôle Actif et Incitatif)">
                      <Textarea rows={7} value={draft.mention_rai}
                                onChange={e => setDraft({ ...draft, mention_rai: e.target.value })}
                                data-testid={`cee-mention-${o.oblige_id}`} />
                      <PlaceholderChips onInsert={(ph) => setDraft(d => ({ ...d, mention_rai: (d.mention_rai || '') + ph }))} />
                    </Field>
                    <div className="flex justify-between gap-2">
                      <Button onClick={() => remove(o.oblige_id, o.nom)} variant="outline" size="sm"
                              className="text-red-600 hover:bg-red-50 border-red-200">
                        <Trash2 className="w-4 h-4 mr-1.5" /> Supprimer l'obligé
                      </Button>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => setExpandedId(null)}>Annuler</Button>
                        <Button onClick={() => saveEdit(o.oblige_id)} size="sm"
                                className="bg-red-600 hover:bg-red-700 text-white"
                                data-testid={`cee-save-${o.oblige_id}`}>
                          <Save className="w-4 h-4 mr-1.5" /> Enregistrer
                        </Button>
                      </div>
                    </div>

                    {/* --------- Tarifs CEE de cet obligé --------- */}
                    <TarifsPanel
                      oblige={o}
                      tarifs={ownTarifs}
                      onChanged={load}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sous-panneau Tarifs (classique + précarité) d'un obligé
// ---------------------------------------------------------------------------
const EMPTY_TARIF = {
  fiche_code: 'all',
  eur_per_mwh_cumac: 0,
  eur_per_mwh_cumac_precarite: 0,
  valid_from: new Date().toISOString().slice(0, 10),
  valid_until: '',
  conditions: '',
  active: true,
};

function TarifsPanel({ oblige, tarifs, onChanged }) {
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const openNew = () => setEditing({
    ...EMPTY_TARIF,
    delegataire_id: oblige.oblige_id,
    delegataire_nom: oblige.nom,
  });

  const openEdit = (t) => setEditing({ ...EMPTY_TARIF, ...t });

  const save = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const payload = { ...editing };
      if (!payload.valid_until) delete payload.valid_until;
      if (payload.tarif_id) {
        await api.put(`/cee/tarifs/${payload.tarif_id}`, payload);
      } else {
        await api.post('/cee/tarifs', payload);
      }
      toast.success('Tarif enregistré');
      setEditing(null);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur enregistrement');
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Supprimer ce tarif ?')) return;
    try {
      await api.delete(`/cee/tarifs/${id}`);
      toast.success('Tarif supprimé');
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur suppression'); }
  };

  const fmtEur = (n) => n == null || n === '' ? '—'
    : `${new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n)} €/MWh`;

  return (
    <div className="border-t-2 border-dashed border-slate-200 pt-4 mt-2">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
          <Euro className="w-4 h-4 text-emerald-600" /> Tarifs CEE
        </h3>
        <Button size="sm" variant="outline" onClick={openNew}
                data-testid={`cee-tarif-add-${oblige.oblige_id}`}>
          <Plus className="w-3.5 h-3.5 mr-1" /> Nouveau tarif
        </Button>
      </div>

      {tarifs.length === 0 && !editing && (
        <p className="text-xs text-slate-500 italic py-2">
          Aucun tarif pour cet obligé — ajoutez-en un pour qu'il apparaisse dans les
          simulations et dossiers CEE.
        </p>
      )}

      {tarifs.length > 0 && (
        <div className="overflow-hidden rounded-md border border-slate-200">
          <table className="w-full text-xs">
            <thead className="bg-slate-100 text-slate-600">
              <tr>
                <th className="text-left px-3 py-2">Fiche</th>
                <th className="text-right px-3 py-2">Classique</th>
                <th className="text-right px-3 py-2">Précarité</th>
                <th className="text-left px-3 py-2">Validité</th>
                <th className="text-center px-3 py-2">Actif</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {tarifs.map((t) => (
                <tr key={t.tarif_id}
                    className={`border-t border-slate-200 ${t.active ? '' : 'opacity-50'}`}>
                  <td className="px-3 py-2 font-mono">
                    {t.fiche_code === 'all' ? 'Toutes' : t.fiche_code}
                  </td>
                  <td className="px-3 py-2 text-right font-mono">{fmtEur(t.eur_per_mwh_cumac)}</td>
                  <td className="px-3 py-2 text-right font-mono">
                    {t.eur_per_mwh_cumac_precarite ? fmtEur(t.eur_per_mwh_cumac_precarite)
                      : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-3 py-2 text-slate-600">
                    du {t.valid_from}{t.valid_until ? ` → ${t.valid_until}` : ''}
                  </td>
                  <td className="px-3 py-2 text-center">
                    {t.active ? <span className="text-emerald-600 font-semibold">✓</span>
                      : <span className="text-slate-400">✗</span>}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => openEdit(t)} className="text-blue-600 hover:underline mr-2">
                      Éditer
                    </button>
                    <button onClick={() => remove(t.tarif_id)} className="text-red-600 hover:underline">
                      Suppr.
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <div className="mt-3 rounded-lg border-2 border-emerald-200 bg-emerald-50/50 p-4 space-y-3"
             data-testid={`cee-tarif-form-${oblige.oblige_id}`}>
          <div className="grid sm:grid-cols-3 gap-3">
            <Field label="Fiche CEE">
              <select
                value={editing.fiche_code}
                onChange={(e) => setEditing({ ...editing, fiche_code: e.target.value })}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white"
              >
                <option value="all">Toutes les fiches</option>
                <option value="BAR-TH-171">BAR-TH-171 (résidentiel)</option>
                <option value="BAT-TH-134">BAT-TH-134 (tertiaire)</option>
                <option value="BAT-EN-101">BAT-EN-101 (tertiaire)</option>
                <option value="BAT-EN-103">BAT-EN-103 (tertiaire)</option>
              </select>
            </Field>
            <Field label="Tarif classique (€/MWh cumac) *">
              <Input type="number" step="0.01"
                     value={editing.eur_per_mwh_cumac}
                     onChange={(e) => setEditing({ ...editing, eur_per_mwh_cumac: parseFloat(e.target.value) || 0 })} />
            </Field>
            <Field label="Tarif précarité (€/MWh cumac)">
              <Input type="number" step="0.01"
                     value={editing.eur_per_mwh_cumac_precarite}
                     onChange={(e) => setEditing({ ...editing, eur_per_mwh_cumac_precarite: parseFloat(e.target.value) || 0 })} />
            </Field>
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <Field label="Valide du *">
              <Input type="date" value={editing.valid_from}
                     onChange={(e) => setEditing({ ...editing, valid_from: e.target.value })} />
            </Field>
            <Field label="Valide jusqu'au">
              <Input type="date" value={editing.valid_until || ''}
                     onChange={(e) => setEditing({ ...editing, valid_until: e.target.value })} />
            </Field>
            <Field label="Actif">
              <label className="inline-flex items-center gap-2 pt-2">
                <input type="checkbox" checked={editing.active}
                       onChange={(e) => setEditing({ ...editing, active: e.target.checked })} />
                <span className="text-sm text-slate-600">Utilisable dans les simulations</span>
              </label>
            </Field>
          </div>
          <Field label="Conditions">
            <Textarea rows={2} value={editing.conditions || ''}
                      onChange={(e) => setEditing({ ...editing, conditions: e.target.value })}
                      placeholder="Ex : Coup de pouce chauffage Bleu/Jaune ANAH, contrat 2026-2028…" />
          </Field>
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setEditing(null)}>Annuler</Button>
            <Button onClick={save} disabled={saving}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white" size="sm"
                    data-testid={`cee-tarif-save-${oblige.oblige_id}`}>
              <Save className="w-4 h-4 mr-1.5" /> {saving ? 'Enregistrement…' : 'Enregistrer'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">{label}</label>
      {children}
    </div>
  );
}

function PlaceholderChips({ onInsert }) {
  const chips = [
    { label: '{montant}', hint: 'Montant de la prime CEE' },
    { label: '{obligé}', hint: 'Nom de l\'obligé' },
    { label: '{siren}', hint: 'SIREN de l\'obligé' },
  ];
  return (
    <div className="flex items-center gap-1.5 mt-2 flex-wrap">
      <span className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Insérer :</span>
      {chips.map(c => (
        <button
          key={c.label}
          type="button"
          onClick={() => onInsert(c.label)}
          title={c.hint}
          className="text-[11px] font-mono text-red-700 bg-red-50 hover:bg-red-100 border border-red-200 rounded px-2 py-0.5 transition"
        >
          {c.label}
        </button>
      ))}
    </div>
  );
}
